import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import toast from 'react-hot-toast';
import { X, Mail, Phone, MapPin, User, Building, Save, Loader2 } from 'lucide-react';
import './ProfileModal.css';

const ProfileModal = ({ isOpen, onClose }) => {
    const [profile, setProfile] = useState(null);
    const [properties, setProperties] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    
    // Editable fields
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [hasChanges, setHasChanges] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [phoneError, setPhoneError] = useState('');

    useEffect(() => {
        if (isOpen) {
            loadProfile();
        }
    }, [isOpen]);

    const loadProfile = async () => {
        try {
            setIsLoading(true);
            const data = await api.getMyProfile();
            setProfile(data.user);
            setProperties(data.properties || []);
            setEmail(data.user.email || '');
            setPhone(data.user.phone || '');
            setHasChanges(false);
            setEmailError('');
            setPhoneError('');
        } catch (error) {
            console.error('Failed to load profile:', error);
            toast.error('Failed to load profile.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        setHasChanges(true);
        // Clear error when user types
        if (emailError) setEmailError('');
    };

    const handlePhoneChange = (e) => {
        // Only allow numbers, spaces, dashes, parentheses, and + sign
        const filtered = e.target.value.replace(/[^0-9\s\-() +]/g, '');
        // Extract digits only and limit to 10
        const digitsOnly = filtered.replace(/\D/g, '');
        if (digitsOnly.length <= 10) {
            setPhone(filtered);
            setHasChanges(true);
            // Clear error when user types
            if (phoneError) setPhoneError('');
        }
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone) => {
        // Remove all non-digit characters for validation
        const digitsOnly = phone.replace(/\D/g, '');
        // Must be exactly 10 digits
        return digitsOnly.length === 10;
    };

    const handleSave = async () => {
        // Reset errors
        setEmailError('');
        setPhoneError('');
        
        let hasValidationErrors = false;
        const trimmedEmail = email.trim();
        const trimmedPhone = phone.trim();

        // Validate email - required and must be valid
        if (!trimmedEmail) {
            setEmailError('Email is required.');
            hasValidationErrors = true;
        } else if (!validateEmail(trimmedEmail)) {
            setEmailError('Please enter a valid email address.');
            hasValidationErrors = true;
        }

        // Validate phone - required and must be exactly 10 digits
        if (!trimmedPhone) {
            setPhoneError('Phone number is required.');
            hasValidationErrors = true;
        } else if (!validatePhone(trimmedPhone)) {
            setPhoneError('Phone number must be exactly 10 digits.');
            hasValidationErrors = true;
        }

        if (hasValidationErrors) {
            return;
        }

        // Check if anything actually changed
        const originalEmail = profile.email || '';
        const originalPhone = profile.phone || '';
        if (trimmedEmail === originalEmail && trimmedPhone === originalPhone) {
            toast('No changes to save.');
            return;
        }

        try {
            setIsSaving(true);
            const updatedUser = await api.updateMyProfile({ 
                email: trimmedEmail, 
                phone: trimmedPhone 
            });
            setProfile(updatedUser);
            setHasChanges(false);
            toast.success('Profile updated successfully.');
        } catch (error) {
            console.error('Failed to update profile:', error);
            toast.error(error.message || 'Failed to update profile.');
        } finally {
            setIsSaving(false);
        }
    };

    const getInitials = () => {
        if (!profile) return '?';
        const first = profile.first_name ? profile.first_name[0] : '';
        const last = profile.last_name ? profile.last_name[0] : '';
        return `${first}${last}`.toUpperCase() || '?';
    };

    if (!isOpen) return null;

    return (
        <div className="profile-modal-overlay" onClick={onClose}>
            <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
                <div className="profile-modal-header">
                    <h2>My Profile</h2>
                    <button className="profile-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="profile-modal-content">
                    {isLoading ? (
                        <div className="profile-loading">
                            <Loader2 size={32} className="spinner" />
                            <p>Loading profile...</p>
                        </div>
                    ) : profile ? (
                        <>
                            {/* Avatar Section */}
                            <div className="profile-avatar-section">
                                <div className="profile-avatar-large">
                                    {getInitials()}
                                </div>
                                <div className="profile-name-role">
                                    <h3>{profile.first_name} {profile.last_name}</h3>
                                    <span className="profile-role-badge">{profile.role}</span>
                                    {profile.employee_code && (
                                        <span className="profile-employee-code">ID: {profile.employee_code}</span>
                                    )}
                                </div>
                            </div>

                            {/* Editable Fields */}
                            <div className="profile-section">
                                <h4>Contact Information</h4>
                                <div className="profile-fields">
                                    <div className="profile-field">
                                        <label htmlFor="profile-email">
                                            <Mail size={16} />
                                            <span>Email <span className="required">*</span></span>
                                        </label>
                                        <input
                                            id="profile-email"
                                            type="email"
                                            value={email}
                                            onChange={handleEmailChange}
                                            className={`profile-input ${emailError ? 'input-error' : ''}`}
                                        />
                                        {emailError && <span className="profile-error">{emailError}</span>}
                                    </div>
                                    <div className="profile-field">
                                        <label htmlFor="profile-phone">
                                            <Phone size={16} />
                                            <span>Phone <span className="required">*</span></span>
                                        </label>
                                        <input
                                            id="profile-phone"
                                            type="tel"
                                            value={phone}
                                            onChange={handlePhoneChange}
                                            className={`profile-input ${phoneError ? 'input-error' : ''}`}
                                            placeholder="10-digit phone number"
                                            maxLength={12}
                                        />
                                        {phoneError && <span className="profile-error">{phoneError}</span>}
                                    </div>
                                </div>
                            </div>

                            {/* Assigned Properties */}
                            <div className="profile-section">
                                <h4>Assigned Properties</h4>
                                {properties.length === 0 ? (
                                    <p className="profile-no-data">No properties assigned.</p>
                                ) : (
                                    <div className="profile-properties">
                                        {properties.map((prop) => (
                                            <div key={prop.id} className="profile-property-card">
                                                <div className="property-card-header">
                                                    <Building size={16} />
                                                    <span className="property-name">{prop.name}</span>
                                                    <span className="property-code">({prop.code})</span>
                                                </div>
                                                {prop.address && (
                                                    <div className="property-address">
                                                        <MapPin size={14} />
                                                        <span>{prop.address}, {prop.city}, {prop.state} {prop.zip_code}</span>
                                                    </div>
                                                )}
                                                {prop.managers && prop.managers.length > 0 && (
                                                    <div className="property-managers">
                                                        <User size={14} />
                                                        <span>Managers: {prop.managers.map(m => m.name).join(', ')}</span>
                                                    </div>
                                                )}
                                                {prop.employee_identifier && (
                                                    <div className="property-employee-id">
                                                        <span>Employee ID: {prop.employee_identifier}</span>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <p className="profile-no-data">Failed to load profile.</p>
                    )}
                </div>

                {profile && (
                    <div className="profile-modal-footer">
                        <button className="profile-btn-cancel" onClick={onClose}>
                            Close
                        </button>
                        <button 
                            className="profile-btn-save" 
                            onClick={handleSave}
                            disabled={!hasChanges || isSaving}
                        >
                            {isSaving ? (
                                <>
                                    <Loader2 size={16} className="spinner" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Save Changes
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProfileModal;
